import { useEffect, useMemo } from "react";
import { useObject, useSheet, useObjectCallback } from "../../../../hooks/useTheatre";
import { findObjectStartsWithKey } from "../../../../utils/FindObjects";
import { types } from "@theatre/core";
import MeasureLabel from "../MeasureLabel";
import { Html, Line, Text } from "@react-three/drei";
import { useImperativeHandle } from "react";
import { forwardRef } from "react";
import { useRef } from "react";
import { degToRad } from "three/src/math/MathUtils";
import { Vector3 } from "three";
import CanvasUiContainer from "../../CanvasUiContainer";
import DescriptionBox from "../../DescriptionBox";

const HowitworksProcess1animationConfig = {
  heatIntesity: 0,
  lightIntesity: 0,
  trace: types.number(0, { range: [0, 1] }),
  glowPosition: types.number(0, { range: [0, 1] }),
  coilPosition: types.number(0, { range: [-0.007, 0.007] }),
  coil2active: types.boolean(true),
  label: 0,
  labelValue: 25,
};

const HowitworksProcess1 = ({ scene, root, ...props }) => {
  const labelRef = useRef();

  const sheet = useSheet("EFD", "HOWITWORKS_PROCESS_1", "0");

  const [workpiece1, workpiece2, coil1, coil2] = useMemo(() => {
    var wp1 = findObjectStartsWithKey("ME_HowItWorks_Workpiece_1", scene);
    var wp2 = findObjectStartsWithKey("ME_HowItWorks_Workpiece_2", scene);
    var coil1 = findObjectStartsWithKey("CoilRoot_1", scene);
    var coil2 = findObjectStartsWithKey("CoilRoot_2", scene);

    return [wp1, wp2, coil1, coil2];
  }, []);

  useEffect(() => {
    sheet.sequence.position = 0;
    sheet.sequence.play({ iterationCount: Infinity });

    return () => {
      sheet.sequence.position = 0;
      // sheet.sequence.pause();
      coil2.visible = true;
      // workpiece2.visible = true;
    };
  }, []);

  useObjectCallback(
    sheet,
    "Animation",
    HowitworksProcess1animationConfig,
    ({ heatIntesity, lightIntesity, trace, glowPosition, coilPosition, coil2active, label, labelValue }) => {
      // if (Light) {
      //   Light.intensity = lightIntesity;
      // }
      if (workpiece1) {
        workpiece1.morphTargetInfluences = [glowPosition, trace];
        // workpiece2.morphTargetInfluences = [glowPosition, trace];
      }

      if (workpiece1.material.userData.shader) {
        workpiece1.material.userData.shader.uniforms.uHeat.value = heatIntesity;
      }
      // if (workpiece2.material.userData.shader) {
      //   workpiece2.material.userData.shader.uniforms.uHeat.value = heatIntesity;
      // }
      if (coil1) {
        coil1.position.y = coilPosition;
      }
      if (coil2) {
        // coil2.visible = coil2active;
        // workpiece2.visible = coil2active;
      }
      if (labelRef.current) {
        labelRef.current.fadeIn(label);
        labelRef.current.setTextValue(`${Math.floor(labelValue)}°`);
      }
    },
  );

  const labeltarget = useMemo(() => {
    return findObjectStartsWithKey("Process_1_LabelTarget", scene);
  }, []);

  return (
    <group
      // position={root.position}
      rotation={root.rotation}
      scale={root.scale}
    >
      <LineLabel position={labeltarget.position} rotation={labeltarget.rotation} ref={labelRef} />
      <CanvasUiContainer>
        <DescriptionBox>L4_INDUCTION_HEATING_CROSS_SECTION_DESCRIPTION</DescriptionBox>
      </CanvasUiContainer>
    </group>
  );
};

export const LineLabel = forwardRef(({ right = false, bottom = false, text = "", ...props }, ref) => {
  const lineRef = useRef();
  const textRef1 = useRef();
  const textRef2 = useRef();

  useImperativeHandle(ref, () => ({
    fadeIn: (v) => {
      if (lineRef.current) {
        lineRef.current.material.opacity = v;
        textRef2.current.material.opacity = v;
      }
    },
    setTextValue: (v) => {
      if (textRef2.current) {
        textRef2.current.text = v;
        textRef1.current.text = v;
      }
    },
  }));

  const linePoints = useMemo(() => {
    var points = [new Vector3(0, 0, 0)];

    const lineLength = 0.3;
    const underLineLength = 0.2;

    if (right) {
      var point1 = new Vector3(-lineLength, lineLength, 0);
      if (bottom) {
        point1.y = -point1.y;
      }
      points.push(point1);
      var point2 = point1.clone().add(new Vector3(-underLineLength, 0, 0));
      points.push(point2);
    } else {
      var point1 = new Vector3(lineLength, lineLength, 0);
      if (bottom) {
        point1.y = -point1.y;
      }
      points.push(point1);
      var point2 = point1.clone().add(new Vector3(underLineLength, 0, 0));
      points.push(point2);
    }

    return points;
  }, []);

  return (
    <group {...props}>
      <group rotation={[0, 0, 0]}>
        <Line points={linePoints} color="#d6d6d6" linewidth={1.5} transparent ref={lineRef} />

        <group position={linePoints[1]}>
          <Text
            rotation={[degToRad(180), 0, degToRad(180)]}
            anchorX={right ? "left" : "right"}
            anchorY="bottom"
            fontSize={0.075}
            color="#fff"
            ref={textRef2}
            position={[0, 0.025, 0]}
          >
            <meshStandardMaterial attach="material" toneMapped={false} transparent color="#cccccc" opacity={2} />
            {text}
          </Text>
        </group>
        <group position={linePoints[1]}>
          <Text
            position={[0, 0.025, 0]}
            rotation={[degToRad(180), degToRad(180), degToRad(180)]}
            anchorX={right ? "right" : "left"}
            anchorY="bottom"
            fontSize={0.075}
            color="#fff"
            ref={textRef1}
          >
            <meshStandardMaterial attach="material" toneMapped={false} transparent color="#cccccc" opacity={2} />
            {text}
          </Text>
        </group>
      </group>
    </group>
  );
});

export default HowitworksProcess1;
